import ImgixWrapper from '@common/components/ImgixWrapper';
import { useTranslations } from '@lang';
import { gridTheme } from '@utils/grid';
import { getCheapestOption } from '@utils/helpers';
import 'lazysizes';
import 'lazysizes/plugins/parent-fit/ls.parent-fit';
import { Moment } from 'moment';
import React from 'react';
import TextTruncate from 'react-text-truncate';
import { Link } from 'shared-routing';
import { DiscountBanner, FromPrice } from '../../Price';
import { ProductGridItemType } from '../ProductGrid.types';
import { NoAvailability } from './NoAvailability';
import {
  CallToActionTitleWrapper,
  DurationInfo,
  MetadataRow,
  ProductBoxStyled,
  ProductInfo,
  StyledImagePlaceholder,
  StyledProductBoxImage,
  StyledProductBoxOption,
  StyledProductBoxPrice,
  StyledProductTitle,
  TitleRow,
} from './ProductBox.styles';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const MAX_IMAGE_SIZE = 467;
const IMAGE_RATIO = 2 / 3;

type ProductBoxProps = {
  product: ProductGridItemType;
  route: string;
  renderMetadataRow?: (product: ProductGridItemType) => React.ReactNode;
  hideOptionsRow?: boolean;
  date?: Moment;
  itemIsUnavailable?: boolean;
  index?: number;
};

/**
 * @deprecated use common/features/List/ListItem
 */
const ProductBox: React.FC<ProductBoxProps> = ({
  product,
  route,
  renderMetadataRow,
  hideOptionsRow = false,
  date,
  itemIsUnavailable,
  index,
}) => {
  const { t } = useTranslations();
  const imageUrl = product?.imageGallery?.length
    ? product.imageGallery?.[0]?.url
    : product?.image?.url;
  const options = 'options' in product && product.options;
  const hasProductOptions = options && options?.length > 0;
  const showCTA = useFeatureIsOn('feature.listing-cta');

  const { price, discount, name, id, duration, durationText, numberOfGuests, minimumGuests } =
    product;
  return (
    <Link
      route={route}
      params={{ id }}
      id={`product-link-${index}`}
      data-cy="product-box_link"
      data-test={name}
      data-test-id={id}
    >
      <ProductBoxStyled aria-label={product.name} data-cy="product-box_container">
        {imageUrl ? (
          <StyledProductBoxImage isTransparent={itemIsUnavailable} data-cy="product-box_image">
            <DiscountBanner discount={discount} />
            <ImgixWrapper
              data-cy="product-box_imgix-wrapper"
              src={imageUrl}
              alt={name}
              width={MAX_IMAGE_SIZE}
              height={Math.round(MAX_IMAGE_SIZE * IMAGE_RATIO)}
              sizes={`
                (min-width: ${gridTheme.breakpoints.sm}px) ${MAX_IMAGE_SIZE}px),
                100vw
              `}
              imgixParams={{
                fit: 'crop',
                crop: 'entropy',
                ar: '3:2',
              }}
            />
          </StyledProductBoxImage>
        ) : (
          <StyledImagePlaceholder data-cy="product-box_image-placeholder">
            <DiscountBanner discount={discount} />
          </StyledImagePlaceholder>
        )}
        {renderMetadataRow && <MetadataRow>{renderMetadataRow(product)}</MetadataRow>}
        <ProductInfo data-cy="product-info">
          <DurationInfo data-cy="duration-info">
            {duration
              ? t('spa.minutesIndicator', { number: duration })
              : durationText && t(durationText)}
          </DurationInfo>
          <TitleRow>
            <StyledProductTitle data-cy="product-title" data-testid={`${route}-title`}>
              <TextTruncate line={2} truncateText="..." text={name} />
            </StyledProductTitle>
            {showCTA && (
              <CallToActionTitleWrapper
                data-cy="product-box_cta"
                title={t('common.view', { itemName: name })}
              >
                {t('common.view.plain')}
              </CallToActionTitleWrapper>
            )}
          </TitleRow>
          <StyledProductBoxOption data-cy="product-box-option">
            {hasProductOptions && !hideOptionsRow && <>{getCheapestOption(options).name}</>}
          </StyledProductBoxOption>
          <StyledProductBoxPrice data-cy="product-box-price">
            <FromPrice
              price={price}
              discount={discount}
              numberOfGuests={numberOfGuests || minimumGuests}
            />
            <NoAvailability date={date} display={itemIsUnavailable} />
          </StyledProductBoxPrice>
        </ProductInfo>
      </ProductBoxStyled>
    </Link>
  );
};

export { ProductBox };
